import {
    Button,
    Col,
    Form,
    Input,
    message,
    Row,
    Select,
    Spin,
    Switch,
    Upload,
} from 'antd'
import find from 'lodash/find'
import trim from 'lodash/trim'
import split from 'lodash/split'
import React, { forwardRef, useCallback, useEffect, useState } from 'react'
import * as com from '../../../Common'
import AddressAutocomplete from '../../../Common/AddressAutocomplete'
import CropperModal from '../../../Photo/CropperModal'
import { track } from 'statsig'
import { STATSIG, PAGE } from 'statsig/events'
import './Company.css'

const { TextArea } = Input
const { Option } = Select

const LICENSE_STATES = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AS', label: 'American Samoa' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'DC', label: 'District Of Columbia' },
    { value: 'FM', label: 'Federated States Of Micronesia' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'GU', label: 'Guam' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MH', label: 'Marshall Islands' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'MP', label: 'Northern Mariana Islands' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'PR', label: 'Puerto Rico' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VI', label: 'Virgin Islands' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
]

const CompanySetup = forwardRef(
    ({ info, finish, change, changeProgress }, ref) => {
        const [form] = Form.useForm()
        const [messageApi, contextHolder] = message.useMessage()
        const [loading, setLoading] = useState(false)
        const [cropperVisible, setCropperVisible] = useState(false)
        const [imageSrc, setImageSrc] = useState('')
        const [showUploadBtn, setShowUploadBtn] = useState(false)
        const [loanLimits, setLoanLimits] = useState({})
        const [error, setError] = useState(null)
        const [counties, setCounties] = useState([])
        const [address, setAddress] = useState('')

        useEffect(() => {
            track(STATSIG.COMMON.VIEW, PAGE.COMPANY_PROFILE, { page: PAGE.COMPANY_PROFILE })
            getCompanyProfile()
            getLoanLimits()
        }, [])

        const getLoanLimits = useCallback(async () => {
            try {
                const response = await fetch('/data/loanlimits')
                if (!response.ok) {
                    throw new Error(response.statusText)
                }
                const data = await response.json()
                setLoanLimits(data)
            } catch (err) {
                console.error('Failed to fetch loan limits:', err)
                setError('Failed to load loan limits data')
            }
        }, [])

        const parseAddress = useCallback((data) => {
            const address = data.Address
            let city = data.City
            let state = data.State
            let zip = data.Zip
            if (!address || typeof address !== 'string') {
                return { city, state, zip }
            }

            const trimmed = trim(address)
            if (!trimmed) {
                return { city, state, zip }
            }

            const parts = split(trimmed, ',').map((p) => trim(p)).filter(Boolean)
            if (parts.length <= 1) {
                return { city, state, zip }
            }

            city = parts[parts.length - 2]
            const lastPart = parts[parts.length - 1]

            // 1. Try to match state abbreviation + zip (e.g. CA 92101 or CA)
            let match = lastPart.match(/^([A-Z]{2})(?:\s+(\d{5}))?$/)
            if (match) {
                state = match[1]
                zip = match[2] || ''
            } else {
                // 2. Try to match full state name + zip (e.g. California 90001 or California)
                // First find the longest matching label in LICENSE_STATES
                let found = null
                for (const stateObj of LICENSE_STATES) {
                    // Allow case-insensitive matching
                    if (
                        lastPart
                            .toLowerCase()
                            .startsWith(stateObj.label.toLowerCase())
                    ) {
                        found = stateObj
                        break
                    }
                }
                if (found) {
                    state = found.value
                    // Extract zip
                    const zipMatch = lastPart.match(/\b(\d{5})\b/)
                    zip = zipMatch ? zipMatch[1] : ''
                } else {
                    // 3. Only state abbreviation or full name entered, no zip
                    // Abbreviation
                    if (/^[A-Z]{2}$/.test(lastPart)) {
                        state = lastPart
                        zip = ''
                    } else {
                        // Full name
                        const stateObj = find(
                            LICENSE_STATES,
                            (s) =>
                                s.label.toLowerCase() === lastPart.toLowerCase()
                        )
                        if (stateObj) {
                            state = stateObj.value
                            zip = ''
                        } else {
                            // Invalid format
                            return { city, state, zip }
                        }
                    }
                }
            }

            return { city, state, zip }
        }, [])

        const getCompanyProfile = useCallback(async () => {
            try {
                setLoading(true)
                const token = sessionStorage.getItem('ZeitroA')
                const response = await fetch('/los/getcompanyprofilesetup', {
                    cache: 'no-cache',
                    method: 'GET',
                    headers: {
                        Authorization: 'Bearer ' + token,
                        Cache: 'no-cache',
                    },
                })

                if (!response.ok) {
                    throw new Error('Failed to fetch company profile')
                }

                const data = await response.json()
                const { city, state, zip } = parseAddress(data)
                const formdata = {
                    file: data.Logo ? [{ url: data.Logo }] : [],
                    name: data.Name,
                    link: data.Link,
                    banner: data.Banner,
                    introduction: data.Introduction,
                    address: data.Address,
                    state: data.State || state,
                    county: data.County,
                    city: data.City || city,
                    zip: data.Zip || zip,
                    phone: data.Phone,
                    noreplyEmail: data.NoreplyEmail,
                    supportEmail: data.SupportEmail,
                    companyNmls: data.CompanyNMLS,
                    licenseId: data.LicenseId,
                }

                if (
                    com.getCustomerId() === 'googain' ||
                    com.getCustomerId() === 'gmcc'
                ) {
                    formdata.showCompanyInfo = data.ShowCompanyInfo
                    formdata.showRate = data.ShowRate
                    formdata.showAffordability = data.ShowAffordability
                }

                setAddress(data.Address || '')
                setShowUploadBtn(data.Logo === '')
                form.setFieldsValue(formdata)
            } catch (error) {
                console.error('Failed to get company profile:', error)
            } finally {
                setLoading(false)
            }
        }, [form])

        const updateCompanyProfile = useCallback(
            async (data) => {
                try {
                    setLoading(true)
                    const token = sessionStorage.getItem('ZeitroA')
                    const response = await fetch(
                        '/los/updatecompanyprofilesetup',
                        {
                            method: 'POST',
                            body: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + token,
                            },
                        }
                    )

                    if (!response.ok) {
                        throw new Error('Failed to update company profile')
                    }

                    track(STATSIG.COMPANY_PROFILE.UPDATE)
                    await getCompanyProfile()
                    messageApi.success('Company profile updated successfully')
                } catch (error) {
                    console.error('Failed to update company profile:', error)
                } finally {
                    setLoading(false)
                }
            },
            [getCompanyProfile, messageApi]
        )

        const validateMessages = {
            required: '${label} is required!',
            types: {
                email: '${label} is not a valid email!',
            },
        }

        const readFileAsDataURL = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = (e) => resolve(e.target.result)
                // reader.onload = (e) => resolve(e.target.result.replace("data:", "").replace(/^.+,/, ""))
                reader.onerror = (error) => reject(error)
            })
        }

        const handleUploadChange = useCallback(
            async ({ file, fileList }) => {
                if (file.status === 'done') {
                    const base64Data = await readFileAsDataURL(
                        file.originFileObj
                    )
                    const newFileList = fileList.map((f) => {
                        if (f.uid === file.uid) {
                            return { ...f, url: base64Data }
                        }
                        return f
                    })
                    form.setFieldsValue({
                        file: newFileList,
                    })
                    setImageSrc(base64Data)
                    setCropperVisible(true)
                } else if (file.status === 'error') {
                }
            },
            [form]
        )

        const normFile = useCallback((e) => {
            if (Array.isArray(e)) {
                return e
            }
            return e?.fileList
        }, [])

        const handleCustomRequest = useCallback((options) => {
            const { onSuccess } = options
            onSuccess()
        }, [])

        const uploadBtn = () => {
            return (
                showUploadBtn && (
                    <div className="upload-btn">
                        <img src="/images/upload-img.svg" />
                    </div>
                )
            )
        }

        const handleCropper = useCallback(() => {
            const base64Data = form.getFieldValue('file')[0].url
            setImageSrc(base64Data)
            setCropperVisible(true)
        }, [form])

        const itemRender = (originNode, file, fileList, actions) => (
            <div style={{ marginTop: '6px', display: 'flex' }}>
                <img
                    onClick={handleCropper}
                    className="profile-photo"
                    src={file.url}
                ></img>
                <img
                    className="remove-photo"
                    src="/images/remove.svg"
                    onClick={() => {
                        actions.remove(file)
                    }}
                />
            </div>
        )

        const handleValuesChange = useCallback(
            async (changedValues, allValues) => {
                if (changedValues?.file?.length > 0) {
                    setShowUploadBtn(false)
                } else {
                    setShowUploadBtn(true)
                }
            },
            [setShowUploadBtn]
        )

        const validateForm = useCallback(async () => {
            try {
                const values = await form.validateFields()
                const data = {
                    Name: values.name,
                    Banner: values.banner,
                    Introduction: values.introduction,
                    Link: values.link,
                    Logo: values.file.length > 0 ? values.file[0].url : '',
                    Address: values.address,
                    State: values.state,
                    County: values.county,
                    City: values.city,
                    Zip: values.zip,
                    Phone: values.phone,
                    NoreplyEmail: values.noreplyEmail,
                    SupportEmail: values.supportEmail,
                    CompanyNMLS: values.companyNmls,
                    LicenseId: values.licenseId,
                }

                if (
                    com.getCustomerId() === 'googain' ||
                    com.getCustomerId() === 'gmcc'
                ) {
                    data.ShowCompanyInfo = values.showCompanyInfo
                    data.ShowRate = values.showRate
                    data.ShowAffordability = values.showAffordability
                }

                await updateCompanyProfile(data)
            } catch (error) {
                console.error('Form validate failed:', error)
            }
        }, [form, updateCompanyProfile])

        const handleSave = useCallback(
            (croppedImage) => {
                const formValues = form.getFieldValue()
                const imgList = form.getFieldValue('file')
                const newFileList = imgList.map((f) => {
                    return { url: croppedImage }
                })
                form.setFieldsValue({
                    file: newFileList,
                })
            },
            [form]
        )

        // Get county list
        const getCountyOptions = useCallback(
            (st) => {
                try {
                    const options = []
                    options.push({ value: '', label: '' })
                    let s = st.toUpperCase()
                    if (st.length === 2) {
                        s = com.convertAbbStateToFull(st).toUpperCase()
                    }

                    if (s in loanLimits) {
                        const keys = Object.keys(loanLimits[s])
                        keys.forEach((x) => {
                            options.push({
                                value: com.capitalize(x),
                                label: com.capitalize(x),
                            })
                        })
                    }
                    setCounties(options)
                } catch (err) {
                    console.error('Failed to get county options:', err)
                    setError('Failed to load county options')
                }
            },
            [loanLimits]
        )

        // Handle state selection change
        const handleStateChange = useCallback(
            (value, option) => {
                try {
                    form.setFieldsValue({
                        state: value,
                        county: '',
                        city: '',
                        zip: '',
                    })
                    getCountyOptions(option.children.toUpperCase())
                } catch (err) {
                    console.error('Failed to handle state change:', err)
                    setError('Failed to update state')
                }
            },
            [form, getCountyOptions]
        )

        // Handle address autocompletion
        const handleAddressCompletion = useCallback(
            (address) => {
                try {
                    const city = com.getSubaddress(
                        address,
                        'locality',
                        'long_name'
                    )
                    const state = com.getSubaddress(
                        address,
                        'administrative_area_level_1',
                        'short_name'
                    )
                    const zip = com.getSubaddress(
                        address,
                        'postal_code',
                        'long_name'
                    )
                    const county = com.getSubaddress(
                        address,
                        'administrative_area_level_2',
                        'long_name'
                    )

                    if (state) {
                        getCountyOptions(
                            com
                                .capitalize(com.convertAbbStateToFull(state))
                                .toUpperCase()
                        )
                    }

                    const num = com.getSubaddress(
                        address,
                        'street_number',
                        'long_name'
                    )
                    const short = com.getSubaddress(
                        address,
                        'route',
                        'short_name'
                    )
                    const stradd = `${num} ${short}, ${city}, ${state} ${zip}`
                    setAddress(stradd)
                    form.setFieldsValue({
                        address: stradd,
                        state: state,
                        county: county,
                        city: city,
                        zip: zip,
                    })
                } catch (err) {
                    console.error('Failed to handle address completion:', err)
                    setError('Failed to process address')
                }
            },
            [form]
        )

        if (error) {
            return <div>{error}</div>
        }

        return (
            <div className="company-info-setup p-4">
                {contextHolder}
                <Spin spinning={loading}>
                    <div className="form-wrap">
                        <Form
                            className="company-info-form"
                            layout="vertical"
                            form={form}
                            onValuesChange={handleValuesChange}
                            validateMessages={validateMessages}
                            size="large"
                        >
                            <Row gutter={32}>
                                <Col span={24} sm={14}>
                                    <Form.Item name="name" label="Company name">
                                        <Input placeholder="" maxLength={40} />
                                    </Form.Item>
                                    <Form.Item
                                        name="link"
                                        label="Company website link"
                                    >
                                        <Input placeholder="" maxLength={200} />
                                    </Form.Item>
                                </Col>
                                <Col span={24} sm={10}>
                                    <Form.Item
                                        name="file"
                                        label="Company logo"
                                        valuePropName="fileList"
                                        getValueFromEvent={normFile}
                                    >
                                        <Upload
                                            maxCount={1}
                                            customRequest={handleCustomRequest}
                                            itemRender={itemRender}
                                            onChange={handleUploadChange}
                                        >
                                            {uploadBtn()}
                                        </Upload>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={32}>
                                <Col span={12} sm={10}>
                                    <Form.Item
                                        name="companyNmls"
                                        label="Company NMLS"
                                    >
                                        <Input placeholder="" maxLength={200} />
                                    </Form.Item>
                                </Col>
                                <Col span={12} sm={10}>
                                    <Form.Item
                                        name="licenseId"
                                        label="Company state license ID"
                                    >
                                        <Input placeholder="" maxLength={200} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={32}>
                                <Col span={24} sm={20}>
                                    <Form.Item
                                        name="address"
                                        label="Company address"
                                    >
                                        <AddressAutocomplete
                                            id="companyAddress"
                                            label=""
                                            name="addressline"
                                            defaultValue={address}
                                            onChange={(e) => {
                                                const value = e.target.value
                                                setAddress(value)
                                                form.setFieldsValue({
                                                    address: value,
                                                })
                                            }}
                                            placeholder="Enter the address"
                                            goodfeedback=""
                                            badfeedback="Please provide your street address."
                                            onCompletion={
                                                handleAddressCompletion
                                            }
                                            size="md"
                                        />
                                        <Row
                                            gutter={16}
                                            style={{
                                                marginTop: 8,
                                            }}
                                        >
                                            <Col span={6} sm={6}>
                                                <Form.Item
                                                    name="state"
                                                    style={{ margin: 0 }}
                                                >
                                                    <Select
                                                        placeholder="State"
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        onChange={
                                                            handleStateChange
                                                        }
                                                        showSearch
                                                        optionFilterProp="children"
                                                    >
                                                        {LICENSE_STATES.map(
                                                            (state) => (
                                                                <Option
                                                                    key={
                                                                        state.value
                                                                    }
                                                                    value={
                                                                        state.value
                                                                    }
                                                                >
                                                                    {
                                                                        state.label
                                                                    }
                                                                </Option>
                                                            )
                                                        )}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6} sm={6}>
                                                <Form.Item
                                                    name="county"
                                                    style={{ margin: 0 }}
                                                >
                                                    <Select
                                                        placeholder="County"
                                                        style={{
                                                            width: '100%',
                                                        }}
                                                        showSearch
                                                        optionFilterProp="children"
                                                    >
                                                        {counties.map(
                                                            (county) => (
                                                                <Option
                                                                    key={
                                                                        county.value
                                                                    }
                                                                    value={
                                                                        county.value
                                                                    }
                                                                >
                                                                    {
                                                                        county.label
                                                                    }
                                                                </Option>
                                                            )
                                                        )}
                                                    </Select>
                                                </Form.Item>
                                            </Col>
                                            <Col span={6} sm={6}>
                                                <Form.Item
                                                    name="city"
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input placeholder="City" />
                                                </Form.Item>
                                            </Col>
                                            <Col span={6} sm={6}>
                                                <Form.Item
                                                    name="zip"
                                                    style={{ margin: 0 }}
                                                >
                                                    <Input placeholder="Zip" />
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={32}>
                                <Col span={12} sm={10}>
                                    <Form.Item
                                        name="phone"
                                        label="Company phone"
                                    >
                                        <Input placeholder="" maxLength={20} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={32}>
                                <Col span={12} sm={10}>
                                    <Form.Item
                                        name="noreplyEmail"
                                        label="Company's noreply email"
                                    >
                                        <Input placeholder="" maxLength={200} />
                                    </Form.Item>
                                </Col>
                                <Col span={12} sm={10}>
                                    <Form.Item
                                        name="supportEmail"
                                        label="Company's support email"
                                    >
                                        <Input placeholder="" maxLength={200} />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Form.Item name="banner" label="Company banner">
                                <Input placeholder="" maxLength={200} />
                            </Form.Item>
                            <Form.Item
                                name="introduction"
                                label="Company introduction "
                            >
                                <TextArea placeholder="" maxLength={500} />
                            </Form.Item>
                            {(com.getCustomerId() === 'googain' ||
                                com.getCustomerId() === 'gmcc') && (
                                <Row>
                                    <Form.Item
                                        name="showCompanyInfo"
                                        label="Show company profile"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item
                                        name="showRate"
                                        label="Show mortgage calculator"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                    <Form.Item
                                        name="showAffordability"
                                        label="Show affordability calculator"
                                        valuePropName="checked"
                                    >
                                        <Switch />
                                    </Form.Item>
                                </Row>
                            )}
                        </Form>
                        <Button type="primary" onClick={validateForm}>
                            Submit
                        </Button>
                    </div>
                </Spin>
                <CropperModal
                    visible={cropperVisible}
                    imageSrc={imageSrc}
                    onClose={() => setCropperVisible(false)}
                    onSave={handleSave}
                />
            </div>
        )
    }
)

export default CompanySetup
